(() => {
  if (customElements.get('kov-reviews')) return;

  class KovReviews extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-review-config]').textContent);
      this.rail = this.querySelector('.dm-rv__rail');
      this.previous = this.querySelector('[data-dm-vprev]');
      this.next = this.querySelector('[data-dm-vnext]');
      this.more = this.querySelector('[data-review-more]');
      this.status = this.querySelector('[data-review-status]');
      this.page = 0;
      this.total = Number(this.dataset.reviewCount) || 0;
      this.seen = new Set();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.previous.addEventListener('click', () => this.scrollRail(-1), options);
      this.next.addEventListener('click', () => this.scrollRail(1), options);
      this.more.addEventListener('click', () => this.load(this.page + 1, true), options);
      this.rail.addEventListener('scroll', () => this.updateNavigation(), { ...options, passive: true });
      this.addEventListener('contentvisibilityautostatechange', () => this.updateNavigation(), options);
      this.resize = new ResizeObserver(() => this.updateNavigation());
      this.resize.observe(this.rail);
      this.querySelectorAll('.dm-rv-card').forEach(card => this.resize.observe(card));
      this.syncHistogramFromTemplate();
      this.more.hidden = false;
      this.load(1);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.resize?.disconnect();
      this.ready = false;
    }

    text(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[[${name}]]`, String(value)), this.config.text[key] || '');
    }

    element(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined && text !== null) element.textContent = String(text);
      return element;
    }

    plainText(html) {
      const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
      doc.querySelectorAll('script,style,iframe,object').forEach(node => node.remove());
      return doc.body.textContent.trim();
    }

    safeUrl(value, image = false) {
      if (!value) return '';
      try {
        const url = new URL(value, location.origin);
        if (url.protocol !== 'https:') return '';
        if (!image && url.origin !== location.origin) return '';
        return url.href;
      } catch { return ''; }
    }

    stars(rating, large = false) {
      const score = Math.max(0, Math.min(5, Number(rating) || 0));
      const stars = this.element('span', `dm-stars${large ? ' dm-stars--lg' : ''}`);
      stars.style.setProperty('--pct', `${score * 20}%`);
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.text('stars', { rating: score }));
      stars.append(this.element('i'));
      return stars;
    }

    card(review) {
      const card = this.element('article', 'dm-rv-card');
      card.dataset.reviewId = String(review.uuid || '');
      const name = review.is_anonymous_reviewer ? this.text('anonymous') : (review.reviewer_name || this.text('customer'));
      const productTitle = this.config.productId ? this.config.productTitle : review.product_title;
      const productUrl = this.safeUrl(this.config.productId ? this.config.productUrl : review.product_url);
      const firstPicture = review.pictures_urls?.[0];
      const pictureUrl = this.safeUrl(typeof firstPicture === 'string' ? firstPicture : firstPicture?.small || firstPicture?.original, true);
      if (pictureUrl) {
        const frame = this.element('div', 'dm-rv-card__img');
        const image = this.element('img');
        Object.assign(image, { src: pictureUrl, alt: this.text('photo', { product: productTitle || '' }), width: 640, height: 480, loading: 'lazy', decoding: 'async' });
        frame.append(image);
        card.append(frame);
      }
      const top = this.element('div', 'dm-rv-card__top');
      top.append(this.stars(review.rating));
      const date = new Date(review.created_at);
      if (!Number.isNaN(date.getTime())) {
        const time = this.element('time', 'dm-rv-card__date', new Intl.DateTimeFormat(this.config.locale || 'en-NZ', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Pacific/Auckland' }).format(date));
        time.dateTime = date.toISOString();
        top.append(time);
      }
      card.append(top);
      if (review.title) card.append(this.element('h3', '', review.title));
      const body = review.body ?? this.plainText(review.body_html);
      if (body) card.append(this.element('p', '', body));
      if (productUrl && productTitle) {
        const link = this.element('a', 'dm-rv-card__product', this.text('on') + ' ');
        link.href = productUrl;
        link.append(this.element('b', '', productTitle));
        card.append(link);
      }
      const footer = this.element('div', 'dm-rv-card__foot');
      const avatar = this.element('span', 'dm-rv-card__avatar', name.trim().slice(0, 1).toUpperCase());
      avatar.setAttribute('aria-hidden', 'true');
      const who = this.element('span', 'dm-rv-card__who');
      who.append(this.element('span', 'dm-rv-card__name', name));
      const meta = this.element('span', 'dm-rv-card__meta', review.location || '');
      if (review.verified_buyer === true) meta.append(this.element('span', 'dm-rv-card__verified', this.text('verified')));
      who.append(meta);
      footer.append(avatar, who);
      card.append(footer);
      return card;
    }

    parseHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.from(doc.querySelectorAll('.jdgm-rev')).map(node => {
        const text = selector => node.querySelector(selector)?.textContent.trim() || '';
        const product = node.querySelector('.jdgm-rev__prod-link');
        const rawDate = node.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content') || '';
        return {
          uuid: node.getAttribute('data-review-id'),
          rating: Number(node.querySelector('.jdgm-rev__rating')?.getAttribute('data-score')),
          title: text('.jdgm-rev__title'), body: text('.jdgm-rev__body'),
          reviewer_name: text('.jdgm-rev__author'), location: text('.jdgm-rev__location'),
          verified_buyer: node.getAttribute('data-verified-buyer') === 'true',
          created_at: rawDate.replace(' UTC', 'Z').replace(' ', 'T'),
          product_title: product?.textContent.trim(), product_url: product?.getAttribute('href'),
          pictures_urls: Array.from(node.querySelectorAll('.jdgm-rev__pic-img')).map(image => image.getAttribute('data-src') || image.getAttribute('src')).filter(Boolean)
        };
      });
    }

    histogram(rows) {
      rows.forEach(item => {
        const rating = Number(item.rating);
        const percent = Math.max(0, Math.min(100, Number(item.percentage) || 0));
        const row = this.querySelector(`[data-dm-rating="${rating}"]`);
        if (!row) return;
        row.querySelector('.dm-rv__fill').style.width = `${percent}%`;
        row.querySelector('u').textContent = `${percent}%`;
      });
    }

    syncHistogramFromTemplate() {
      const source = this.querySelector('.dm-jdgm-summary-source')?.content;
      if (source) this.histogram(Array.from(source.querySelectorAll('.jdgm-histogram__row')).map(row => ({ rating: row.dataset.rating, percentage: row.dataset.percentage })));
    }

    updateSummary(data) {
      if (!this.config.productId) return;
      const rating = Number(data.average_rating) || 0;
      const count = Number(data.number_of_reviews) || 0;
      const summary = this.querySelector('.dm-rv__summary');
      if (summary) {
        summary.hidden = count === 0;
        summary.querySelector('[data-review-average]').textContent = rating.toFixed(2).replace(/0$/, '').replace(/\.0$/, '');
        summary.querySelector('[data-review-stars]').replaceChildren(this.stars(rating, true));
        summary.querySelector('[data-review-count]').textContent = this.text(count === 1 ? 'oneReview' : 'reviews', { count });
        this.histogram(data.histogram || []);
      }
      document.querySelectorAll('[data-kov-product-rating]').forEach(badge => {
        if (badge.dataset.productId !== String(this.config.productId)) return;
        badge.hidden = count === 0;
        badge.querySelector('.dm-stars').style.setProperty('--pct', `${rating * 20}%`);
        badge.querySelector('.dm-stars').setAttribute('aria-label', this.text('stars', { rating }));
        badge.querySelector('.dm-pdp__rating-count').textContent = this.text(count === 1 ? 'oneReview' : 'reviews', { count });
        badge.href = `#${this.id}`;
      });
    }

    async load(page, interactive = false) {
      if (this.loading) return;
      this.loading = true;
      this.more.disabled = true;
      this.more.textContent = this.text('loading');
      this.status.textContent = '';
      this.request = new AbortController();
      const timer = setTimeout(() => this.request.abort(), 12000);
      try {
        const endpoint = this.config.productId ? 'reviews_for_widget' : 'all_reviews_js_based';
        const url = new URL(`https://cdn.judge.me/reviews/${endpoint}`);
        const params = { shop_domain: this.config.shop, platform: 'shopify', page, per_page: this.config.pageSize, sort_by: 'created_at', sort_dir: 'desc' };
        if (this.config.productId) params.product_id = this.config.productId;
        else params.review_type = 'product-reviews';
        Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, String(value)));
        const response = await fetch(url, { signal: this.request.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Review request failed');
        const data = await response.json();
        if (this.config.productId && String(data.product_external_id) !== String(this.config.productId)) throw new Error('Product mismatch');
        const reviews = Array.isArray(data.reviews) ? data.reviews : typeof data.html === 'string' ? this.parseHtml(data.html) : null;
        if (!reviews) throw new Error('Invalid review response');
        if (!this.isConnected) return;
        const total = this.config.productId ? data.number_of_reviews : data.number_of_product_reviews;
        if (total !== undefined) this.total = Number(total) || 0;
        if (page === 1) {
          this.rail.replaceChildren();
          this.seen.clear();
        }
        const firstAdded = this.rail.children.length;
        reviews.forEach(review => {
          const id = String(review.uuid || '');
          if (!id || this.seen.has(id)) return;
          this.seen.add(id);
          const card = this.card(review);
          this.rail.append(card);
          this.resize.observe(card);
        });
        this.page = Number(data.pagination?.current_page || data.page || page);
        this.updateSummary(data);
        if (!this.seen.size) {
          const card = this.element('article', 'dm-rv-card dm-rv-card--empty');
          card.append(this.element('h3', '', this.text('emptyTitle')), this.element('p', '', this.text('emptyBody')));
          this.rail.append(card);
        }
        const addedReviews = this.rail.children.length - firstAdded;
        this.more.hidden = addedReviews === 0 || this.seen.size >= this.total || reviews.length === 0 || (data.pagination && this.page >= Number(data.pagination.total_pages));
        this.status.textContent = this.total > 0 ? this.text('showing', { shown: this.seen.size, total: this.total }) : '';
        this.more.textContent = this.text('loadMore');
        if (interactive && this.rail.children[firstAdded]) this.rail.scrollTo({ left: this.rail.children[firstAdded].offsetLeft - this.rail.offsetLeft, behavior: this.motion() });
      } catch (error) {
        if (!this.isConnected) return;
        this.more.hidden = false;
        this.more.textContent = this.text('retry');
        this.status.textContent = this.text('error');
      } finally {
        clearTimeout(timer);
        this.loading = false;
        this.more.disabled = false;
        this.updateNavigation();
      }
    }

    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }

    scrollRail(direction) {
      this.rail.scrollBy({ left: direction * Math.max(280, this.rail.clientWidth * 0.8), behavior: this.motion() });
    }

    updateNavigation() {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      this.previous.disabled = max < 3 || this.rail.scrollLeft <= 2;
      this.next.disabled = max < 3 || this.rail.scrollLeft >= max - 2;
    }
  }

  customElements.define('kov-reviews', KovReviews);
})();
